import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Divider, Select, InputNumber, Typography } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlugCircleCheck } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
import { useLazyGetAdaptersSummaryQuery } from "../../../redux/adapters/adapterAPI";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// INTERFACES
import { AdaptersAssignAdapterInterface } from "../../../interfaces/Adapter.interface";
import { RootState } from "../../../app/store";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import { fetchEveryPage } from "../../../utils/fetchEveryPage";
import { logError } from "../../../utils/devLog";

function AdaptersAssignAdapter({
  liftType,
  setFloorsAndDirectionData,
  adapterId,
  formAction,
  lifts,
  stops,
  maxStops,
  floorsBelowLobby,
}: AdaptersAssignAdapterInterface) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");
  const { Option } = Select;

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [data, setData] = useState<any>({
    numberOfLifts: 0,
    numberOfStops: 2,
    maxNumberOfStops: 0,
    numberOfFloorsBelowLobby: 0,
    liftDirection: "NA",
    configurationId: "",
    adapterId: "",
  });
  const [adapters, setAdapters] = useState<any>([]);
  const [loadingAdapters, setLoadingAdapters] = useState(false);
  const [adapterSelected, setAdapterSelected] = useState("");
  const [liftDirection, setLiftDirection] = useState<any>([]);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const { openModalCreateEditBank } = useSelector((state: RootState) => state.bank);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetAdapters] = useLazyGetAdaptersSummaryQuery();

  // ************************************************ */
  // FUNCTIONS ************************************** */

  const initData = async () => {
    setLoadingAdapters(true);
    try {
      const token = await GETJwtToken();
      // The server clamps every page, however large a limit is asked for, so
      // the whole list is only reachable by paging through it.
      const allAdapters = await fetchEveryPage((page, limit) =>
        triggerGetAdapters({ page, limit, token }).unwrap(),
      );
      setAdapters(allAdapters);
    } catch (err) {
      logError("[AdaptersAssignAdapter] fetch adapters failed:", err);
    } finally {
      setLoadingAdapters(false);
    }
  };

  const onChangeData = (newValue: any, nameValue: any) => {
    const copy = structuredClone(data);
    copy[nameValue] = newValue;
    setData(copy);
  };

  const onChangeAdapter = (newValue: string) => {
    setAdapterSelected(newValue);
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    if (openModalCreateEditBank) {
      initData();
    }
  }, [openModalCreateEditBank]);

  useEffect(() => {
    if (formAction === "EDIT" && adapterId !== "") {
      setAdapterSelected(adapterId);
    }
  }, [adapterId]);

  useEffect(() => {
    const copy = structuredClone(data);
    copy.adapterId = adapterSelected;
    // The bank form gates its MCE-only "Event Reporting Address" input on the
    // assigned adapter's protocol, so surface it alongside the id. `adapters`
    // is a dependency because in EDIT mode adapterSelected is seeded before
    // the summary query resolves.
    //
    // `adapters` comes from useLazyGetAdaptersSummaryQuery, whose response is
    // FlatBuffer-decoded through the generated AdapterSummaryT Object API —
    // that surfaces the field as camelCase `protocolType`, not the snake_case
    // `protocol_type` the raw JSON wire format uses. Reading `protocol_type`
    // here always resolved to undefined regardless of the adapter picked, so
    // the bank form's MCE-only field never appeared. Falling back to
    // `protocol_type` keeps this working if the query ever falls back to raw
    // JSON (no registered FlatBuffer schema handler).
    const selectedAdapter = adapters.find((item: any) => item.id === adapterSelected);
    copy.adapterProtocol = selectedAdapter?.protocolType ?? selectedAdapter?.protocol_type ?? "";
    setData(copy);
  }, [adapterSelected, adapters]);

  useEffect(() => {
    // Update lif type acording lift selection
    if (liftType === LiftType.ESCALATOR) {
      setLiftDirection(["UP", "DOWN"]);
      setData({
        numberOfLifts: lifts,
        numberOfStops: stops,
        maxNumberOfStops: maxStops,
        numberOfFloorsBelowLobby: floorsBelowLobby,
        liftDirection: "UP",
        configurationId: "",
        adapterId: "",
      });
    } else if (liftType === LiftType.MOVINGWALK) {
      setLiftDirection(["FORWARD", "BACKWARD"]);
      setData({
        numberOfLifts: lifts,
        numberOfStops: stops,
        maxNumberOfStops: maxStops,
        numberOfFloorsBelowLobby: floorsBelowLobby,
        liftDirection: "FORWARD",
        configurationId: "",
        adapterId: "",
      });
    } else if (liftType === LiftType.ELEVATOR) {
      setData({
        numberOfLifts: lifts,
        numberOfStops: stops,
        maxNumberOfStops: maxStops,
        numberOfFloorsBelowLobby: floorsBelowLobby,
        liftDirection: "NA",
        configurationId: "",
        adapterId: "",
      });
    }
  }, [liftType]);

  useEffect(() => {
    setFloorsAndDirectionData(data);
  }, [data]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <Divider orientation="left" className="generalStyles__drawerDivider">
        <h5>
          <FontAwesomeIcon
            icon={faPlugCircleCheck}
            className="generalStyles__info generalStyles__mrFix"
          />
          {t("general.adapter")}
        </h5>
      </Divider>
      <div className={`drawer__box__${theme}`}>
        {/** NAME */}
        <div className="generalStyles__flex drawer__container">
          <div>
            <p className="drawer__title">
              <span className="generalStyles__inputFlexRequired">*</span>
              {t("general.name")}
            </p>
          </div>
          <div className="generalStyles__width100 drawer__textContainer generalStyles__flex">
            <Select
              className="generalStyles__width100"
              size="small"
              onChange={(e: any) => {
                onChangeAdapter(e);
              }}
              value={adapterSelected}
              loading={loadingAdapters}
            >
              {adapters.map((item: any) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                  {item.version ? (
                    <Typography.Text type="secondary"> · {item.version}</Typography.Text>
                  ) : null}
                </Option>
              ))}
            </Select>
            <div className="generalStyles__infoTooltipIconBlank" />
          </div>
        </div>
        {/** Configuration type ELEVATOR */}
        {liftType === LiftType.ELEVATOR && formAction === "CREATE" && (
          <>
            {/** TOTAL LIFTS */}
            <div className="generalStyles__flex drawer__container mt-2">
              <div>
                <p className="drawer__title">
                  {" "}
                  <span className="generalStyles__inputFlexRequired">*</span>
                  Total {t("configuration.configurationFiles.lifts")}
                </p>
              </div>
              <div className="generalStyles__width100 drawer__textContainer generalStyles__flex">
                <InputNumber
                  className="generalStyles__inputNumberFull"
                  precision={0}
                  value={data.numberOfLifts}
                  onChange={(e: any) => {
                    onChangeData(e, "numberOfLifts");
                  }}
                  size="small"
                  disabled={adapterSelected === ""}
                />
                <div className="generalStyles__infoTooltipIconBlank" />
              </div>
            </div>
            {/** STOPS */}
            <div className="generalStyles__flex drawer__container mt-2">
              <div>
                <p className="drawer__title">
                  {" "}
                  <span className="generalStyles__inputFlexRequired">*</span>
                  {t("configuration.configurationFiles.stops")}
                </p>
              </div>
              <div className="generalStyles__width100 drawer__textContainer generalStyles__flex">
                <InputNumber
                  className="generalStyles__inputNumberFull"
                  precision={0}
                  value={data.numberOfStops}
                  onChange={(e: any) => {
                    onChangeData(e, "numberOfStops");
                  }}
                  size="small"
                  disabled={adapterSelected === ""}
                  min={2}
                />
                <div className="generalStyles__infoTooltipIconBlank" />
              </div>
            </div>
          </>
        )}
        {/** ESCALATOR / MOVINGWALK DIRECTION */}
        {(liftType === LiftType.ESCALATOR || liftType === LiftType.MOVINGWALK) &&
          formAction === "CREATE" && (
            <div className="generalStyles__flex drawer__container">
              <div>
                <p className="drawer__title">
                  {" "}
                  <span className="generalStyles__inputFlexRequired">*</span>
                  {t("general.direction")}
                </p>
              </div>
              <div className="generalStyles__width100 drawer__textContainer generalStyles__flex">
                <Select
                  value={data.liftDirection}
                  onChange={(e: any) => {
                    onChangeData([e], ["liftDirection"]);
                  }}
                  className="generalStyles__width100"
                  size="small"
                >
                  {liftDirection.map((item: any) => (
                    <Option key={item} value={item}>
                      {item}
                    </Option>
                  ))}
                </Select>
                <div className="generalStyles__infoTooltipIconBlank" />
              </div>
            </div>
          )}
      </div>
    </div>
  );
}

export default AdaptersAssignAdapter;
